
import React, { useState } from 'react';
import { toast } from "@/components/ui/use-toast";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  DndContext,
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  closestCenter,
  DragOverlay,
  DragEndEvent,
  DragStartEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { usePageContext } from '@/hooks/usePageContext';
import { Loader2, Plus, Copy, Edit, Trash2, GripVertical, MoreVertical, Monitor, Smartphone, Eye, EyeOff } from "lucide-react";
import { Page, PagePlatform } from '@/types/page';
import { usePagesManager } from '@/hooks/usePagesManager';
import { useRef } from 'react';
import PageEditor from './PageEditor';
import PrintablePagesReport from './PrintablePagesReport';
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScrollArea } from "@/components/ui/scroll-area";
import SortablePageCard from './page-management/SortablePageCard';

const PagesManager: React.FC = () => {
  const { pages, setPages, titleConfig } = usePageContext();
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [activeTab, setActiveTab] = useState<string>("enabled");
  
  const {
    enabledPages,
    disabledPages,
    handleTogglePage,
    handlePlatformChange,
    handleDragStart,
    handleDragEnd,
    handleCreatePage,
    handleSavePage,
    handleDuplicatePage,
    handleEditPage,
    handleDeletePage,
    handlePrintTimesPages,
    currentEditPage,
    isEditing,
    activeDragId,
    setIsEditing,
    setCurrentEditPage,
    showPrintPreview
  } = usePagesManager({ pages, setPages });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [addPageDialogOpen, setAddPageDialogOpen] = useState(false);

  // Get the page being dragged for the overlay
  const activePageItem = activeDragId ? pages.find(page => page.id === activeDragId) : null;

  // Show toast when page operations are performed
  const showSuccessToast = (message: string) => {
    toast({
      title: message,
      description: "שינויים יוצגו אוטומטית בלוח התצוגה",
    });
  };

  const handleSavePageWithToast = (page: Page) => {
    handleSavePage(page);
    showSuccessToast(`הדף "${page.title}" נשמר בהצלחה`);
  };

  const handleTogglePageWithToast = (id: string) => {
    handleTogglePage(id);
    const page = pages.find(p => p.id === id);
    if (page) {
      showSuccessToast(`הדף "${page.title}" ${page.enabled ? 'הופעל' : 'הושבת'}`);
    }
  };

  const handleDeletePageWithToast = (id: string) => {
    const page = pages.find(p => p.id === id);
    if (page && window.confirm("האם אתה בטוח שברצונך למחוק דף זה?")) {
      handleDeletePage(id);
      showSuccessToast(`הדף "${page.title}" נמחק בהצלחה`);
    }
  };

  const handleDuplicatePageWithToast = (page: Page) => {
    handleDuplicatePage(page);
    showSuccessToast(`הדף "${page.title}" שוכפל בהצלחה`);
  };

  const renderPagesList = (pagesList: Page[], columnId: 'enabled' | 'disabled') => (
    <SortableContext 
      items={pagesList.map(p => p.id)}
      strategy={verticalListSortingStrategy}
      id={columnId}
    >
      <div className="space-y-3">
        {pagesList.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-gray-200 rounded-xl bg-muted/30">
            <p className="text-sm">
              {columnId === 'enabled'
                ? "אין דפים פעילים - הפעל דף כדי לראות אותו כאן"
                : "אין דפים מושבתים"}
            </p>
          </div>
        ) : (
          pagesList.map(page => (
            <SortablePageCard
              key={page.id}
              page={page}
              onToggle={handleTogglePageWithToast}
              onEdit={handleEditPage}
              onDuplicate={handleDuplicatePageWithToast}
              onDelete={handleDeletePageWithToast}
              onPlatformChange={handlePlatformChange}
              onPositionChange={() => {}} // No longer used
              currentColumn={columnId}
              isDraggable={columnId === 'enabled'}
            />
          ))
        )}
      </div>
    </SortableContext>
  );

  return (
    <div className="px-2 md:px-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold">ניהול דפים</h1>
          <p className="text-muted-foreground text-sm mt-1">
            גרור דפים פעילים כדי לשנות את סדר התצוגה
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => setAddPageDialogOpen(true)} className="flex-1 md:flex-none">
            <Plus className="mr-2 h-4 w-4" />
            הוסף דף
          </Button>
          
          <Button variant="outline" onClick={() => handlePrintTimesPages(printRef)} className="flex-1 md:flex-none">
            הדפס דפי זמנים
          </Button>
        </div>
      </div>

      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {isMobile ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="enabled" className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                דפים פעילים
              </TabsTrigger>
              <TabsTrigger value="disabled" className="flex items-center gap-2">
                <EyeOff className="h-4 w-4" />
                דפים מושבתים
              </TabsTrigger>
            </TabsList>
            
            <ScrollArea className="h-[calc(100vh-240px)] mt-4">
              <TabsContent value="enabled" className="m-0">
                {renderPagesList(enabledPages, 'enabled')}
              </TabsContent>
              
              <TabsContent value="disabled" className="m-0">
                {renderPagesList(disabledPages, 'disabled')}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-green-50 text-green-800 px-4 py-3 font-medium flex justify-between items-center">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    דפים פעילים
                  </h2>
                  <Badge variant="outline" className="bg-green-100 text-green-700">
                    {enabledPages.length}
                  </Badge>
                </div>
                <div className="p-4 min-h-[400px]">
                  <ScrollArea className="h-[calc(100vh-300px)]">
                    {renderPagesList(enabledPages, 'enabled')}
                  </ScrollArea>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gray-50 text-gray-800 px-4 py-3 font-medium flex justify-between items-center">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <EyeOff className="h-5 w-5" />
                    דפים מושבתים
                  </h2>
                  <Badge variant="outline" className="bg-gray-100 text-gray-700">
                    {disabledPages.length}
                  </Badge>
                </div>
                <div className="p-4 min-h-[400px]">
                  <ScrollArea className="h-[calc(100vh-300px)]">
                    {renderPagesList(disabledPages, 'disabled')}
                  </ScrollArea>
                </div>
              </div>
            </div>
          </div>
        )}

        <DragOverlay>
          {activePageItem && (
            <SortablePageCard
              page={activePageItem}
              onToggle={() => {}}
              onEdit={() => {}}
              onDuplicate={() => {}}
              onDelete={() => {}}
              onPlatformChange={() => {}}
              onPositionChange={() => {}}
              currentColumn={'enabled'}
              isDragOverlay={true}
              isDraggable={true}
            />
          )}
        </DragOverlay>
      </DndContext>
      
      {/* Print preview container (hidden) */}
      <div 
        ref={printRef} 
        className="hidden"
      >
        {showPrintPreview && (
          <PrintablePagesReport 
            pages={pages.filter(page => page.type === "times" && page.enabled)}
          />
        )}
      </div>

      {/* Page Editor Dialog */}
      {isEditing && currentEditPage && (
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogTitle>עריכת דף</DialogTitle>
            <DialogDescription>
              ערוך את פרטי הדף והתוכן שלו
            </DialogDescription>
            <PageEditor 
              page={currentEditPage} 
              onSave={handleSavePageWithToast}
              onCancel={() => setIsEditing(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Add Page Dialog */}
      <Dialog open={addPageDialogOpen} onOpenChange={setAddPageDialogOpen}>
        <DialogContent>
          <DialogTitle>בחר סוג דף</DialogTitle>
          <DialogDescription>
            בחר את סוג הדף שברצונך ליצור
          </DialogDescription>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            
            <Button 
              variant="outline" 
              className="justify-start h-auto py-3" 
              onClick={() => {
                handleCreatePage('daily-study');
                setAddPageDialogOpen(false);
              }}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">לימוד יומי</span>
                <span className="text-xs text-muted-foreground">שיעורים יומיים</span>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start h-auto py-3" 
              onClick={() => {
                handleCreatePage('video');
                setAddPageDialogOpen(false);
              }}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">וידאו</span>
                <span className="text-xs text-muted-foreground">הצגת סרטון</span>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start h-auto py-3" 
              onClick={() => {
                handleCreatePage('supabase-data');
                setAddPageDialogOpen(false);
              }}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">נתוני מסד נתונים</span>
                <span className="text-xs text-muted-foreground">הצגת נתונים מ-Supabase</span>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start h-auto py-3" 
              onClick={() => {
                handleCreatePage('zmaney-hayom');
                setAddPageDialogOpen(false);
              }}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">זמני היום</span>
                <span className="text-xs text-muted-foreground">הצגת זמני היום לבחירתך</span>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start h-auto py-3" 
              onClick={() => {
                handleCreatePage('times');
                setAddPageDialogOpen(false);
              }}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">זמנים</span>
                <span className="text-xs text-muted-foreground">הצגת זמנים מותאמים אישית</span>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start h-auto py-3" 
              onClick={() => {
                handleCreatePage('preset-api');
                setAddPageDialogOpen(false);
              }}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">מידע API</span>
                <span className="text-xs text-muted-foreground">הצגת מידע מ-API חיצוני</span>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="justify-start h-auto py-3" 
              onClick={() => {
                handleCreatePage('yahrzeit_display');
                setAddPageDialogOpen(false);
              }}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">יארצייט</span>
                <span className="text-xs text-muted-foreground">הצגת שמות לזכר נפטרים</span>
              </div>
            </Button>
          </div>
          <DialogClose asChild>
            <Button variant="outline" className="mt-2 w-full">ביטול</Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagesManager;
